import { useCallback, useRef } from "react";
import { Zip, AsyncZipDeflate } from "fflate";
import streamSaver from "streamsaver";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";
import { ReadContractorCard } from "@/types/contractorCard";
import { buildFileUrl } from "@/lib/utils/buildFileUrl";
import { CONTRACTOR_CARDS_DOWNLOAD_CONCURRENCY } from "../constants";
import { deduplicateFilename, pipeResponseBodyToZipEntry } from "../utils";
import { getFilenameTimestamp } from "../utils/getFilenameTimestamp";

/**
 * Hook for bulk downloading contractor cards as a single streaming ZIP archive.
 * Uses fflate + StreamSaver for low memory usage, supports cancel and progress UI.
 */
export const useContractorCardsBulkDownload = ({
  cards,
}: {
  cards: ReadContractorCard[];
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const operationProgress = useAsyncOperationProgress();
  const abortRef = useRef<AbortController | null>(null);

  /** Cancel ongoing downloads */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
    operationProgress.cancel();
  }, [operationProgress]);

  /** Main bulk download function */
  const downloadBulk = useCallback(async () => {
    const downloadableCards = cards.filter(
      (c) => c.card_s3_key && c.card_filename,
    );
    if (downloadableCards.length === 0) return;

    const abortController = new AbortController();
    abortRef.current = abortController;

    // Initialize progress UI
    operationProgress.initialize(
      downloadableCards.map((c) => ({
        id: c.id,
        label: c.card_filename ?? c.id,
      })),
    );
    const filenameUsageCount = new Map<string, number>();
    const archiveName = `contractor-cards-${getFilenameTimestamp()}.zip`;
    const fileStream = streamSaver.createWriteStream(archiveName);

    try {
      const zip = new Zip();

      const stream = new ReadableStream<Uint8Array>({
        start(controller) {
          operationProgress.startProcessing();

          // Pipe zip chunks to the output stream
          zip.ondata = (err, chunk, final) => {
            if (err) return controller.error(err);
            if (chunk) controller.enqueue(chunk);
            if (final) controller.close();
          };

          let activeFetches = 0;
          let nextIndex = 0;
          let finished = false;

          /** Schedule concurrent downloads up to the defined concurrency */
          const scheduleNext = () => {
            if (finished) return;

            if (abortController.signal.aborted) {
              finished = true;
              zip.end(); // finalize archive on abort
              return;
            }

            while (
              activeFetches < CONTRACTOR_CARDS_DOWNLOAD_CONCURRENCY &&
              nextIndex < downloadableCards.length
            ) {
              const card = downloadableCards[nextIndex++];
              activeFetches++;
              fetchAndAddToZip(card)
                .catch((error) => {
                  // Report error for this item but continue with others
                  operationProgress.markItemProcessed(card.id, false, error);
                })
                .finally(() => {
                  activeFetches--;
                  scheduleNext();
                });
            }

            if (
              activeFetches === 0 &&
              nextIndex >= downloadableCards.length &&
              !finished
            ) {
              finished = true;
              zip.end(); // finalize archive when all done
            }
          };

          /** Fetch a single file and add it to the ZIP */
          const fetchAndAddToZip = async (card: ReadContractorCard) => {
            const { card_s3_key: key, card_filename: filename } = card;

            if (!key || !filename) {
              // This should never happen because of initial filtering
              throw new Error("Missing file metadata");
            }

            const url = buildFileUrl(key);
            if (!url) throw new Error("Missing file URL");

            operationProgress.setCurrentItem(filename);

            const res = await fetch(url, { signal: abortController.signal });
            if (!res.ok) throw new Error(`Failed to fetch file: ${res.status}`);
            if (!res.body) throw new Error("ReadableStream not available");

            const zipEntryName = deduplicateFilename(
              filename,
              filenameUsageCount,
            );

            const zipEntry = new AsyncZipDeflate(zipEntryName);
            zip.add(zipEntry);

            await pipeResponseBodyToZipEntry(res.body.getReader(), zipEntry);

            operationProgress.markItemProcessed(card.id, true);
          };

          scheduleNext();
        },
      });

      // Pipe zip stream directly to disk
      await stream.pipeTo(fileStream);
    } catch (err) {
      if (!abortController.signal.aborted) {
        console.error("Bulk download error:", err);
        toast({
          title: t("contractorCards.download.error.title"),
          description: t("contractorCards.download.error.description"),
          variant: "destructive",
        });
      }
    } finally {
      abortRef.current = null;
      operationProgress.complete();
    }
  }, [cards, operationProgress, t, toast]);

  return { downloadBulk, cancel, operationProgress };
};
